import EditPanelModal from './editPanel/EditPanel.modal';

/* @ngInject */
export default function DashboardService(
  $sails,
  $q,
  DashboardInfoService,
  $uibModal,
  $log,
  $window
) {

  const service = this;

  const DASHBOARD_TEMPLATE = {
    editState: false,
    dashboard: {
      groups: []
    },
    panels: {},
    dashboardLayout: {
      layout: {}
    },
    messages: []
  };

  const backupOfDashboardBeforeEdits = {};
  const currentDashboard = angular.copy(DASHBOARD_TEMPLATE);

  service.getDashboardDetailsFromServer = getDashboardDetailsFromServer;
  service.getUsageForDashboard = getUsageForDashboard;
  service.getLayout = getLayout;
  service.updateLayout = updateLayout;
  service.setState = setState;
  service.getState = getState;
  service.createNewDashboard = createNewDashboard;
  service.setDashboardToEdit = setDashboardToEdit;
  service.getEditDashboard = getEditDashboard;
  service.cancelEditDashboard = cancelEditDashboard;
  service.addPanelToDashboard = addPanelToDashboard;
  service.getCurrentDashboard = getCurrentDashboard;
  service.removePanelFromDashboard = removePanelFromDashboard;
  service.updatePanelOnDashboard = updatePanelOnDashboard;
  service.saveDashboard = saveDashboard;
  service.deleteDashboard = deleteDashboard;
  service.openEditPanelModal = openEditPanelModal;

  async function getDashboardDetailsFromServer(dashboardId) {
    resetEditDashboard();

    const dashboard = await getDashboard(dashboardId);
    const { panels, messages } = dashboard;
    // ? Instead of assigning panels every time we could use a static getter `() => this.dashboard.panels`
    _.assign(currentDashboard, { panels, dashboard, dashboardLayout: await getLayout(dashboardId), messages });
    return currentDashboard;
  }

  function getUsageForDashboard(equipment) {
    return $sails.get(`/equipmentdatausage/getRecentUsage/${equipment.join('|')}`)
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error.data);
      });
  }

  async function getLayout(dashboardId, template = '') {
    const url = `/dashboardlayout/${dashboardId}/${template}`;
    const { data: dashboardLayout } = await $sails.get(url);
    return dashboardLayout;
  }

  function updateLayout(template) {
    const layout = { layout: angular.fromJson(angular.toJson(currentDashboard.dashboardLayout.layout)) };
    const url = template ? `/dashboardlayout/${currentDashboard.dashboard.id}/${template}` :
      `/dashboardlayout/${currentDashboard.dashboard.id}`;

    return $sails.post(url, layout)
      .then((success) => {
        if (template) {
          currentDashboard.dashboard.defaultLayout = success.data.id;
        }
        currentDashboard.dashboardLayout = success.data;
        return success;
      })
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error.data);
      });

  }

  /**
   * @param {string} id Panel
   * @param {object} [newState={}] Panel's state data
   * @returns {object} New state (STM)
   * ! State will override previous so caller should merge current state first if wipe is undesired !
   */
  function setState(id, newState = {}) {
    _.set(currentDashboard, ['dashboardLayout', 'layout', id, 'state'], newState);
    return getState(id);
  }

  /**
   * @param {string} id Panel
   * @returns {object} Panel state (STM)
   */
  function getState(id) {
    return _.cloneDeep(_(currentDashboard.dashboardLayout.layout).get([id, 'state'], {}));
  }

  /**
   * Edit dashboard functions
   */

  function resetEditDashboard() {
    currentDashboard.editState = angular.copy(DASHBOARD_TEMPLATE.editState);
    currentDashboard.dashboard = angular.copy(DASHBOARD_TEMPLATE.dashboard);
    currentDashboard.panels = angular.copy(DASHBOARD_TEMPLATE.panels);
    currentDashboard.dashboardLayout = angular.copy(DASHBOARD_TEMPLATE.dashboardLayout);
    currentDashboard.messages = angular.copy(DASHBOARD_TEMPLATE.messages);
  }

  function generateUUID() {
    /* eslint-disable no-bitwise */
    const sixT = 16;
    const byThr = 0x3;
    const byEi = 0x8;
    let da = Date.now();
    if ($window.performance && _.isFunction($window.performance.now)) {
      da += performance.now(); // use high-precision timer if available
    }
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
      const ra = ((da + Math.random()) * sixT) % sixT | 0;
      da = Math.floor(da / sixT);
      return (ch === 'x' ? ra : (ra & byThr) | byEi).toString(sixT);
    });
    return uuid;
    /* eslint-enable no-bitwise */
  }

  function createNewDashboard() {
    resetEditDashboard();
    currentDashboard.editState = true;
    currentDashboard.dashboard.id = 'NEW_DASH';
    return currentDashboard;
  }

  function setDashboardToEdit(dashboard) {
    currentDashboard.editState = true;
    currentDashboard.dashboard = angular.copy(dashboard.dashboard);
    angular.forEach(dashboard.panels, (panel) => {
      currentDashboard.panels[panel.id] = angular.copy(panel);
    });
    currentDashboard.dashboardLayout = angular.copy(dashboard.dashboardLayout);

    backupOfDashboardBeforeEdits.dashboard = angular.copy(currentDashboard.dashboard);
    backupOfDashboardBeforeEdits.panels = angular.copy(currentDashboard.panels);
    backupOfDashboardBeforeEdits.dashboardLayout = angular.copy(currentDashboard.dashboardLayout);
    backupOfDashboardBeforeEdits.messages = angular.copy(currentDashboard.messages);

    return currentDashboard;
  }

  function getEditDashboard() {
    return currentDashboard.dashboard;
  }

  function cancelEditDashboard() {
    // removes the 'addPanel' from the list of panels on the dashboard since no longer in edit mode
    if (currentDashboard.dashboard.id) {
      currentDashboard.editState = false;
      currentDashboard.dashboard = angular.copy(backupOfDashboardBeforeEdits.dashboard);
      currentDashboard.panels = angular.copy(backupOfDashboardBeforeEdits.panels);
      currentDashboard.dashboardLayout = angular.copy(backupOfDashboardBeforeEdits.dashboardLayout);
      currentDashboard.messages = angular.copy(backupOfDashboardBeforeEdits.messages);
    } else {
      resetEditDashboard();
    }
    currentDashboard.hasBeenChanged = false;
  }

  function addPanelToDashboard(panelToAdd) {
    panelToAdd.id = generateUUID();
    currentDashboard.panels[panelToAdd.id] = panelToAdd;
    currentDashboard.hasBeenChanged = true;
  }

  function getCurrentDashboard() {
    return currentDashboard;
  }

  function removePanelFromDashboard(panelIdToRemove) {
    delete currentDashboard.panels[panelIdToRemove];
    currentDashboard.hasBeenChanged = true;
  }

  function updatePanelOnDashboard(panelToUpdate) {
    currentDashboard.panels[panelToUpdate.id] = angular.copy(panelToUpdate);
    currentDashboard.hasBeenChanged = true;
  }


  async function getDashboard(id) {
    const { data: dashboard } = await $sails.get(`/dashboard/${id}`);
    dashboard.panels = _.omitBy(dashboard.panels ?? {}, 'deleted');
    return dashboard;
  }

  function saveDashboard() {
    let objToSend = {
      panels: currentDashboard.panels,
      groups: currentDashboard.dashboard.groups,
      equipment: _.uniq(_.flattenDeep(_.map(currentDashboard.panels, 'equipment'))),
      name: currentDashboard.dashboard.name,
      id: currentDashboard.dashboard.id,
      organization: currentDashboard.updatedOrganization,
      messages: currentDashboard.messages
    };
    objToSend = angular.copy(objToSend);
    if (objToSend.id === 'NEW_DASH') {
      delete objToSend.id;
      return $sails.post('/dashboard/', objToSend)
        .then(success => DashboardInfoService.refreshDashboardListFromServer()
          .then(() => {
            currentDashboard.dashboard.id = success.data.id;
            currentDashboard.panels = success.data.panels;
            // Notify Angular side nav to refresh
            window.dispatchEvent(new CustomEvent('dashboard-refresh'));
            return success.data.id;
          }))
        .catch((error) => {
          $log.error('Error: ', error);
          return $q.reject(error.data);
        });
    }
    return $sails.put(`/dashboard/${objToSend.id}`, objToSend)
      .then(success => DashboardInfoService.refreshDashboardListFromServer()
        .then(() => {
          // Notify Angular side nav to refresh
          window.dispatchEvent(new CustomEvent('dashboard-refresh'));
          return success.data.id;
        }))
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error.data);
      });

  }

  function deleteDashboard() {
    if (currentDashboard.dashboard.id) {
      return $sails.delete(`/dashboard/${currentDashboard.dashboard.id}`)
        .then(success => DashboardInfoService.refreshDashboardListFromServer()
          .catch(_.noop)
          .then(() => {
            // Notify Angular side nav to refresh
            window.dispatchEvent(new CustomEvent('dashboard-refresh'));
            return success;
          }))
        .catch((error) => {
          $log.error('Error: ', error);
          return $q.reject(error.data);
        });
    }
    return $q.reject('Can not delete a dashboard that has not been created yet');

  }

  function openEditPanelModal(selectedPanelId) {
    return $uibModal.open({
      ...EditPanelModal,
      size: 'ex-lg',
      resolve: {
        panels() {
          return currentDashboard.panels;
        },
        selectedPanelId() {
          if (selectedPanelId) {
            return selectedPanelId;
          }
          return null;

        }
      }
    }).result;
  }
}
